/**
 * Owner "Training courses" section for My Training — each course with its child
 * sessions grouped, a progress line, and a "Cancel course" action (cancels the
 * whole remaining course; individual upcoming sessions are cancelled from the
 * bookings list).
 */
import { CalendarRange, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusPill, type StatusTone } from '@/components/ui/status-pill';
import { form_error_message } from '@/lib/forms';
import { use_my_training_packages, use_cancel_training_package } from '@/features/training-package/hooks';
import type { TrainingPackage, TrainingPackageStatus } from '@/features/training-package/types';
import type { BookingStatus } from '@/features/bookings/types';

const IST = 'Asia/Kolkata';
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const PACKAGE_TONE: Record<TrainingPackageStatus, StatusTone> = {
  PENDING_PAYMENT: 'warning',
  ACTIVE: 'success',
  COMPLETED: 'info',
  CANCELLED: 'destructive',
};

const SESSION_TONE: Record<BookingStatus, StatusTone> = {
  PENDING_PAYMENT: 'warning',
  AWAITING_VENDOR: 'warning',
  CONFIRMED: 'info',
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
  CANCELLED: 'destructive',
  NO_SHOW: 'destructive',
};

/** A course can still be cancelled while it has upcoming (non-started) sessions. */
const CANCELLABLE_PACKAGE: TrainingPackageStatus[] = ['PENDING_PAYMENT', 'ACTIVE'];

function session_when(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', { timeZone: IST, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function PackageCard({ pkg, onCancel, cancelling }: { pkg: TrainingPackage; onCancel: (p: TrainingPackage) => void; cancelling: boolean }) {
  const done = pkg.sessions.filter((s) => s.status === 'COMPLETED').length;
  return (
    <li data-testid={`my-package-${pkg.id}`}>
      <Card>
        <CardContent className="flex flex-col gap-3 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">
                  {[...pkg.days_of_week].sort((a, b) => a - b).map((d) => DAY_LABELS[d]).join(', ')} · {pkg.time}
                </span>
                <StatusPill tone={PACKAGE_TONE[pkg.status]}>{pkg.status.replace(/_/g, ' ').toLowerCase()}</StatusPill>
              </div>
              <p className="text-xs text-muted-foreground">
                {pkg.weeks} week(s) from {pkg.start_date} · {done}/{pkg.total_sessions} sessions done
              </p>
            </div>
            <div className="text-right text-sm">
              <div className="font-heading font-bold tabular-nums">₹{pkg.total_inr}</div>
              {pkg.payment && (
                <div className="text-xs text-muted-foreground">{pkg.payment.status === 'CAPTURED' ? 'Paid' : pkg.payment.status.toLowerCase()}</div>
              )}
            </div>
          </div>

          <ul className="divide-y divide-border rounded-md border border-border" data-testid={`my-package-sessions-${pkg.id}`}>
            {pkg.sessions.map((s) => (
              <li key={s.id} className="flex items-center justify-between px-3 py-2 text-sm">
                <span>{session_when(s.slot_start)}</span>
                <StatusPill tone={SESSION_TONE[s.status]}>{s.status.replace(/_/g, ' ').toLowerCase()}</StatusPill>
              </li>
            ))}
          </ul>

          {CANCELLABLE_PACKAGE.includes(pkg.status) && (
            <div className="flex justify-end">
              <Button size="sm" variant="ghost" onClick={() => onCancel(pkg)} disabled={cancelling} data-testid={`cancel-package-${pkg.id}`}>
                Cancel course
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </li>
  );
}

export function TrainingPackagesSection() {
  const packages_q = use_my_training_packages();
  const cancel_m = use_cancel_training_package();
  const packages = packages_q.data ?? [];

  async function cancel(pkg: TrainingPackage) {
    if (!window.confirm('Cancel this course? All upcoming sessions that haven’t started will be cancelled.')) return;
    const reason = window.prompt('Reason (optional):') ?? '';
    await cancel_m.mutateAsync({ id: pkg.id, reason: reason.trim() || undefined });
  }

  if (packages_q.isLoading) {
    return (
      <div className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading courses…
      </div>
    );
  }
  if (packages_q.error) {
    return <p className="p-2 text-sm text-destructive" data-testid="my-packages-error">{form_error_message(packages_q.error, 'Failed to load courses.')}</p>;
  }
  if (packages.length === 0) return null;

  return (
    <section className="flex flex-col gap-3" data-testid="my-training-packages">
      <h2 className="flex items-center gap-2 font-heading text-lg font-bold">
        <CalendarRange className="h-5 w-5 text-primary" /> Training courses
      </h2>
      <ul className="flex flex-col gap-3" data-testid="my-training-packages-list">
        {packages.map((p) => (
          <PackageCard key={p.id} pkg={p} onCancel={cancel} cancelling={cancel_m.isPending} />
        ))}
      </ul>
    </section>
  );
}
